import type { DeltaStatic } from "quill"
import { Program } from "../../../../../../types/gqlTypes/graphql"
import { DaoState } from "../../../../../snapcourse/db"
import {
  AssetRef,
  CropDataType,
  InteractionOrientation
} from "../../../../db/types"
import { InlineContentType } from "../customWidgets/InlineInteractionContainer/types"

type InteractionProps = {
  attributes?: Record<string | "responseIdentifier", any>
  gapText?: GapText[]
  simpleChoice?: SimpleChoice[]
  simpleMatchSet?: SimpleMatchSet[]
}

export type NeutralSetting = true | false

export type QtiContainerType = "lesson" | "exercise" | "practiceTest"

export type JsonSchemaFormContextType = {
  interactionType: InteractionTypes
  containerType?: QtiContainerType
  initialData: QtiInteractions
  initialResponseDeclaration: ResponseDeclaration[]
  interactionProps: InteractionProps
  dao: DaoState<"block_qti_interactions">
  openSnackbar?: (
    message: string,
    autoHideDuration?: number,
    callback?: () => void
  ) => void
}

export type BaseAttributes = {
  attributes: {
    responseIdentifier: string
    neutralSetting?: NeutralSetting
  }
}

export type GapText = {
  attributes: {
    identifier: string
    // matchGroup: string
    matchMax: number
    matchMin: number
  }
  data: string
  deltas?: { ops: any[] }
}

export type GapContent = {
  // string for just text and gap object for gap answer insert
  insert:
    | string
    | {
        gap: {
          identifier: string
          value: string
        }
      }
}

export type GapMatchInteraction = BaseAttributes & {
  attributes: {
    responseIdentifier: string
    maxRetries: number
    minCorrect: number
    minAssociations: number
    maxAssociations: number
    shuffle: boolean
  }

  prompt: string
  gapText: GapText[]
  content: InlineContentType
}

export type ChoiceInteraction = BaseAttributes & {
  attributes: {
    maxRetries: number
    minCorrect: number
    maxChoices: number
    minChoices: number
    responseIdentifier: string
    shuffle: boolean
    neutralSetting?: NeutralSetting
  }
  prompt: string
  simpleChoice: SimpleChoice[]
}

export type OrderInteraction = BaseAttributes & {
  attributes: {
    responseIdentifier: string
    minChoices: number
    maxChoices: number
    orientation: InteractionOrientation
    shuffle: boolean
    neutralSetting?: NeutralSetting
  }
  prompt: string
  simpleChoice: SimpleChoice[]
}

export type MatchInteraction = BaseAttributes & {
  attributes: {
    maxRetries: number
    minCorrect: number
    maxAssociations: number
    minAssociations: number
    responseIdentifier: string
    shuffle: boolean
  }
  prompt: string
  simpleMatchSet: SimpleMatchSet[]
}

export type SimpleChoice = {
  meta: {
    answerType: ChoiceInteractionAnswersTypes
  }
  attributes: {
    identifier: string
    choiceIndex?: string
  }
  data: string
  deltas?: { ops: any[] }
  asset?: AssetRef
}

export type ExtendedTextInteraction = BaseAttributes & {
  attributes: {
    responseIdentifier: string
  }
  prompt: string
  content: any
}

export type SimpleMatchSet = {
  simpleAssociableChoice: SimpleAssociableChoice[]
}

export type SimpleAssociableChoice = {
  attributes: {
    showHide: "show" | "hide"
    fixed: boolean
    identifier: string
    matchMax: number
    matchMin: number
  }
  data: string
  deltas?: { ops: any[] }
}

export type RichTextContentInteraction =
  | HottextInteraction
  | GapMatchInteraction

export type HottextInteraction = BaseAttributes & {
  attributes: {
    maxChoices: number
    minChoices: number
    maxRetries: number
    minCorrect: number
    responseIdentifier: string
  }
  prompt: string
  content: InlineContentType
}

export type TextEntryInteractiom = {
  attributes: {
    responseIdentifier: string
  }
  prompt: string
  content: InlineContentType
}

export type EquationOperator = {
  attributes: {
    tex: string
  }
}

export type EquationOperators = {
  attributes: {
    id: string
    type?: string
    label?: string
  }
  equationOperator: EquationOperator[]
}

export type EquationEditorSectionType = {
  equationOperators: EquationOperators[]
}

export type EquationEditorInteraction = {
  attributes: {
    id: string
    responseIdentifier: string
    maxRetries: number
  }
  prompt: DeltaStatic
  equationNumbersSection: EquationEditorSectionType
  equationActionsSection?: EquationEditorSectionType
}

export type EquationAnswerType = {
  id: string
  value: string
}

export type Shape = "circle" | "default" | "ellipse" | "poly" | "rect"

export type HotspotChoice = {
  attributes: {
    identifier: string
    shape: Shape
    coords: string
    hotspotLabel?: string
  }
}

export type HotspotInteraction = BaseAttributes & {
  attributes: {
    maxRetries: number
    minCorrect: number
    maxChoices: number
    minChoices: number
    responseIdentifier: string
  }

  prompt: string
  hotspotChoice: HotspotChoice[]
  object: {
    attributes: {
      data: string
      height: number
      type: string
      width: number
      fileName: string
      cropData: CropDataType | null
    }
  }
}

export type InlineChoice = {
  attributes: {
    identifier: string
  }
  data: string
  deltas?: { ops: any[] }
}

export type InlineChoiceInteraction = BaseAttributes & {
  attributes: {
    responseIdentifier: string
  }
  prompt: string
  content: InlineContentType
}

export type InlineInteractions =
  | InlineChoiceInteraction
  | GapMatchInteraction
  | HottextInteraction
  | TextEntryInteractiom

export type BlockType = "text" | "interaction"

export enum InteractionTypes {
  choice = "choiceInteraction",
  match = "matchInteraction",
  gapMatch = "gapMatchInteraction",
  order = "orderInteraction",
  hotspot = "hotspotInteraction",
  extendedText = "extendedTextInteraction",
  hottext = "hottextInteraction",
  custom = "customInteraction",
  equationEditor = "equationEditor",
  customInteraction = "customInteraction",
  inlineChoice = "inlineChoiceInteraction",
  textEntry = "textEntryInteraction"
}

export type InteractionWithType = {
  interaction: QtiInteractions
  type: InteractionTypes
}

export type ChoiceInteractionAnswersTypes =
  | "text"
  | "math"
  | "image"
  | "rich-text"

export enum BaseType {
  boolean = "boolean",
  directedPair = "directedPair",
  duration = "duration",
  file = "file",
  float = "float",
  identifier = "identifier",
  integer = "integer",
  pair = "pair",
  point = "point",
  string = "string",
  uri = "uri"
}

export type MediaObject = {
  attributes: {
    data: string
    type: string
    fileName: string
    cropData?: CropDataType | null
    width: number
    height: number
    alt: string
    id: string
    snap_id: string
  }
}

export enum Cardinality {
  multiple = "multiple",
  single = "single",
  ordered = "ordered",
  record = "record"
}

export type CorrectResponse = {
  value: string[]
}
export type ResponseDeclaration = {
  attributes: {
    identifier: string
    baseType?: BaseType
    cardinality?: Cardinality
  }
  correctResponse: CorrectResponse
  mapping: any
  areaMapping: any
}

export type QtiInteractions =
  | ChoiceInteraction
  | MatchInteraction
  | GapMatchInteraction
  | OrderInteraction
  | ExtendedTextInteraction
  | HotspotInteraction
  | HottextInteraction
  | InlineChoiceInteraction

export type ContentRegistryItem = {
  id: string
  program?: Program // only for records with type type = test
  title?: string
  type: string // test, testPart, assessmentSection, assessmentItem OR interaction type
  attributes: Record<string, any> // key-value storage for element attributes
  content: string[] // JSON representation of nested content
  meta: Record<string, any> // using for different calculations to avoid processing nested content + additional info which can not be included into  package (correct answers, scoring and so on)
  owner_id?: string // id of user who has created this record (if it will be necessary for permissions checks)
  parent_id: string // null for root elements
  created_at?: number
  updated_at?: number
  deleted_at?: number // let’s replace is_deleted flag with deleted_at timestamp to have additional info in one field
}

export type ItemBlock = Omit<ContentRegistryItem, "content"> & {
  content: any
}
export type ItemColumn = Omit<ContentRegistryItem, "content"> & {
  content: ItemBlock[]
}
export type AssessmentItem = Omit<ContentRegistryItem, "content"> & {
  content: ItemColumn[]
  meta: {
    responseDeclaration: ResponseDeclaration[]
  }
}
export type AssessmentSection = Omit<ContentRegistryItem, "content"> & {
  content: AssessmentItem[]
}
export type AssessmentTestPart = Omit<ContentRegistryItem, "content"> & {
  content: AssessmentSection[]
}
export type Assessment = Omit<ContentRegistryItem, "content"> & {
  content: AssessmentTestPart[]
}
